import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../api/apiClient";
import type {
  AuthEvent,
  CatalogAuditAction,
  ShopAuditAction,
  UserActivityEntry,
  UserListEntry,
} from "../../api/types";
import { useAuth } from "../../state/AuthContext";
import { formatDate } from "./teamLabels";
import {
  actionLabel,
  actionTone,
  authEventLabel,
  authEventTone,
  catalogActionLabel,
  catalogActionTone,
  type ActionTone,
} from "../actionLogUi";

/** One vocabulary for all three streams — the Action log page's own. */
function describe(row: UserActivityEntry): { label: string; tone: ActionTone } {
  if (row.stream === "floor") {
    const action = row.action as ShopAuditAction;
    return { label: actionLabel(action), tone: actionTone(action) };
  }
  if (row.stream === "catalog") {
    const action = row.action as CatalogAuditAction;
    return { label: catalogActionLabel(action), tone: catalogActionTone(action) };
  }
  const event = row.action as AuthEvent;
  return { label: authEventLabel(event), tone: authEventTone(event) };
}

/**
 * "2 hours ago" beats a timestamp for scanning a short list; the exact moment
 * is one hover away and the full page has it in a column.
 */
function ago(iso: string): string {
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return iso;
  const mins = Math.round((Date.now() - then) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days} d ago`;
  return new Date(iso).toLocaleDateString();
}

/**
 * G-105 — what a colleague has actually done, on the Team page.
 *
 * Replaces the shop-posting history that used to sit behind "History" (owner,
 * 2026-09-22: *"it should show their activity history. action log filtered by
 * that person"*). It is the same data the Action log page reads, asked
 * per-person rather than per-shop, so the wording comes from `actionLogUi`
 * rather than a second vocabulary that could drift from it.
 *
 * The empty state is the careful part. Only the kiosk and the catalog write
 * audit rows — CRM-side orders, dispatch, schedule edits and team management
 * write none — so an empty list must NOT read as "this person did nothing".
 */
export function TeamActivityList({
  user,
  take = 10,
}: {
  user: UserListEntry;
  take?: number;
}) {
  const { hasRole } = useAuth();
  const isOwner = hasRole("OWNER");
  const [rows, setRows] = useState<UserActivityEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setRows(null);
    setError(null);
    api
      .listUserActivity(user.id, take)
      .then((list) => {
        if (live) setRows(list);
      })
      .catch((err: unknown) => {
        if (!live) return;
        setError(
          err instanceof Error ? err.message : "Could not load the activity",
        );
      });
    return () => {
      live = false;
    };
  }, [user.id, take]);

  /*
   * The full log, already filtered to this person. A 30-day window because the
   * page's own default is "today", which for "show me what they have been
   * doing" is almost always empty.
   *
   * `locationId` is sent ONLY for an owner, who must name a shop there and
   * would otherwise land on the "pick a shop" state. A manager is pinned to
   * their own assignment by the server whatever is passed — and passing this
   * person's shop would actively break it: `locationAssignments[0]` is the
   * TARGET's latest shop, which need not be the manager's, and a foreign id
   * earns a 403 rather than a filtered view (found by /security-review).
   *
   * One link per Action log TAB, not one "See all": this list merges three
   * streams, but the page shows one tab at a time and opens on Floor, so a
   * single link hid every item edit and sign-in the person had just seen
   * (found by /code-review). Sign-ins is drawn for owners only — OD-25.
   */
  const logLink = (tab: "floor" | "catalog" | "security") => {
    const params = new URLSearchParams({
      actorUserId: user.id,
      preset: "30d",
      tab,
    });
    const shop = user.locationAssignments[0]?.location.id;
    if (isOwner && shop && tab === "floor") params.set("locationId", shop);
    return `/app/action-log?${params.toString()}`;
  };

  return (
    <div className="team-activity">
      <div className="team-activity-head">
        <h4 className="team-activity-title">Recent activity</h4>
        <nav className="team-activity-links" aria-label="Open in the Action log">
          <Link className="team-activity-all" to={logLink("floor")}>
            Kiosk
          </Link>
          <Link className="team-activity-all" to={logLink("catalog")}>
            Items
          </Link>
          {isOwner ? (
            <Link className="team-activity-all" to={logLink("security")}>
              Sign-ins
            </Link>
          ) : null}
        </nav>
      </div>

      {error ? <div className="alert error">{error}</div> : null}

      {!error && rows === null ? (
        <p className="muted team-activity-empty">Loading…</p>
      ) : null}

      {!error && rows !== null && rows.length === 0 ? (
        <p className="muted team-activity-empty">
          Nothing recorded yet. Only shop-floor and catalog work is logged —
          orders, deliveries and schedule changes made in the CRM are not,
          so this can be empty for someone who has been busy.
        </p>
      ) : null}

      {!error && rows !== null && rows.length > 0 ? (
        <ul className="team-activity-list">
          {rows.map((row) => {
            const { label, tone } = describe(row);
            return (
              <li key={`${row.stream}-${row.id}`} className="team-activity-row">
                <span className={`team-activity-what tone-${tone}`}>
                  {label}
                </span>
                <span className="team-activity-when" title={formatDate(row.createdAt)}>
                  {ago(row.createdAt)}
                </span>
                {/* Named only when there is one: catalog and security rows
                    happen at no shop, and a floor row's shop matters to an
                    owner reading across several. */}
                <span className="team-activity-where">
                  {row.entityName ?? row.location?.code ?? ""}
                </span>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}
